import React, { useMemo } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import colors from 'core/themes/opencloud/colors'
import components from 'core/themes/opencloud/components'

const getColorBasedOnUtilization = (percent: number) => {
  if (percent < 60) return components.badge.success.color
  if (percent < 80) return components.badge.danger.color
  return components.badge.error.color
}

interface ResourceSummary {
  name: string
  allocated: number
  capacity: number
  utilization: number | null
  utilizationPercentage: number | null
  unit: string
}

interface ResourceUtilizationChartProps {
  resources: ResourceSummary[]
}

type ResourceChartData = ResourceSummary & {
  usagePercentage: number
  remainingPercentage: number
  isNA: boolean
}

type CustomXAxisTickProps = {
  x?: number
  y?: number
  payload?: { value: string }
  data: ResourceChartData[]
}

const CustomXAxisTick: React.FC<CustomXAxisTickProps> = ({ x = 0, y = 0, payload, data }) => {
  const key = payload?.value
  const row = data.find((d) => d.name === key)
  const sub =
    row && row.capacity > 0
      ? `${row.allocated}/${row.capacity} ${row.unit}`
      : `${row.allocated} ${row.unit}`

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#666"
        fontSize={14}
        fontFamily="Fira Sans"
      >
        {key}
      </text>
      <text
        x={0}
        y={20}
        dy={16}
        textAnchor="middle"
        fill="#999"
        fontSize={12}
        fontFamily="Fira Sans"
      >
        {sub}
      </text>
    </g>
  )
}

export default function ResourceUtilizationChart({ resources }: ResourceUtilizationChartProps) {
  const data: ResourceChartData[] = useMemo(() => {
    return resources.map((d) => {
      return {
        ...d,
        usagePercentage: d.utilizationPercentage ?? 0,
        remainingPercentage:
          d.utilizationPercentage != null ? Math.max(0, 100 - (d.utilizationPercentage ?? 0)) : 0,
        isNA: d.utilizationPercentage == null,
      }
    })
  }, [resources])

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        barSize={35}
        barCategoryGap={15}
        barGap={0}
        margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
      >
        <XAxis
          dataKey="name"
          tick={<CustomXAxisTick data={data} />}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide domain={[0, 100]} />
        {/* Utilization Bar */}
        <Bar stackId="stack" dataKey="usagePercentage" isAnimationActive={false}>
          {data.map((r) => (
            <Cell
              key={r.name}
              fill={r.isNA ? colors.grey[100] : getColorBasedOnUtilization(r.usagePercentage ?? 0)}
            />
          ))}
        </Bar>
        {/* Remaining Bar + label */}
        <Bar
          dataKey="remainingPercentage"
          stackId="stack"
          fill={colors.grey[100]}
          isAnimationActive={false}
          label={(props: any) => {
            const { x, y, width, index } = props
            const row = data[index]
            if (!row) return null
            const label = row.isNA ? 'N/A' : `${row.usagePercentage.toFixed(0)}%`
            return (
              <text
                x={x + width / 2}
                y={(y ?? 0) - 10}
                fill="#333"
                textAnchor="middle"
                fontSize={12}
                fontFamily="Fira Sans"
              >
                {label}
              </text>
            )
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
